import { call, put, select } from 'redux-saga/effects';
import { ApiError, ApiResponse } from 'store/api/types';
import { logoutRequest } from 'store/auth/actions';
import { turnMessageOn } from 'store/app/actions';
import { DataLogStatus, SubmissionUpdateField } from 'store/app/enums';
import { MessageData } from 'store/app/types';
import { MessageStates } from 'containers/MessageBox';
import { updateSubmissionLog } from 'store/organisation/actions';
import {
  changeStatusAfterUpdate,
  internallyApproveFailure,
  internallyApproveSuccess,
} from 'store/vat/actions';
import { InternallyApproveRequestAction } from 'store/vat/types';
import { checkResponse } from 'store/utils';
import { isTifdReportPath } from 'utils/tifd-mapping';
import { renderErrorMessage } from 'utils/render-error-message';
import i18n from 'i18next';
import { approveTifdReport } from '../api';
import { getTifdContext, getTifdReport } from '../selectors';

/**
 * Internal approve from the TIFD report page. Returns true when this request
 * belonged to TIFD, including when the call failed. VAT internal approve
 * continues only when this returns false.
 */
/* eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types */
export function* approveTifdReportFromReturnSaga(
  action: InternallyApproveRequestAction
) {
  if (!isTifdReportPath(window.location.pathname)) return false;

  const { dataLogId, reportUrl, mappingId } = action.payload;
  try {
    const context: ReturnType<typeof getTifdContext> = yield select(
      getTifdContext
    );
    const report: ReturnType<typeof getTifdReport> = yield select(getTifdReport);
    const applicationConnectionId = context?.applicationConnectionId;
    const reportId = report?.id || dataLogId;
    if (!applicationConnectionId || !reportId) {
      throw new Error('TIFD report is not ready to approve.');
    }

    const response: ApiResponse = yield call(
      approveTifdReport,
      applicationConnectionId,
      reportId,
      reportUrl
    );

    const err: ApiError | null = checkResponse(response?.responseMessage);
    if (err) throw err;

    const message: MessageData = {
      title: i18n.t('common.success-exclamation'),
      description: response.responseMessage.message,
      type: MessageStates.SUCCESS,
    };

    yield put(turnMessageOn(message));
    yield put(changeStatusAfterUpdate(DataLogStatus.INTERNAL_APPROVED));
    yield put(
      updateSubmissionLog(
        mappingId,
        SubmissionUpdateField.STATUS,
        'INTERNAL_APPROVED',
        dataLogId
      )
    );
    yield put(internallyApproveSuccess());
  } catch (error: any) {
    const message: MessageData | null = renderErrorMessage(error);
    if (message) {
      yield put(turnMessageOn(message));
    } else {
      yield put(logoutRequest());
    }
    yield put(internallyApproveFailure());
  }

  return true;
}
